import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, Wallet, TrendingDown, TrendingUp, PiggyBank, Landmark, Search, CornerDownRight, ChevronDown, ChevronRight, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { listAccounts, createAccount, updateAccount, deleteAccount, importMasterCoa } from '../lib/api';

const TYPE_META = {
  asset:     { label: 'Aset',       icon: Landmark,     color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  liability: { label: 'Liabilitas', icon: TrendingDown, color: 'bg-red-50 border-red-200 text-red-800' },
  equity:    { label: 'Modal',      icon: PiggyBank,    color: 'bg-amber-50 border-amber-200 text-amber-800' },
  revenue:   { label: 'Pendapatan', icon: TrendingUp,   color: 'bg-sky-50 border-sky-200 text-sky-800' },
  expense:   { label: 'Beban',      icon: Wallet,       color: 'bg-stone-100 border-stone-300 text-stone-800' },
};

const TYPE_PREFIX = { asset: '1', liability: '2', equity: '3', revenue: '4', expense: '5' };

const suggestCode = (type, parentId, accounts) => {
  if (parentId) {
    const parent = accounts.find((a) => a.id === parentId);
    if (!parent) return '';
    const base = parent.code;                 // e.g. "1-1100"
    const siblings = accounts.filter((a) => a.parent_id === parentId);
    // Increment last 2 digits under the same parent
    const prefix = base.slice(0, -2);
    let max = parseInt(base.slice(-2), 10) || 0;
    siblings.forEach((s) => {
      if (s.code.startsWith(prefix)) {
        const n = parseInt(s.code.slice(prefix.length), 10);
        if (!isNaN(n)) max = Math.max(max, n);
      }
    });
    return `${prefix}${String(max + 1).padStart(2, '0')}`;
  }
  const p = TYPE_PREFIX[type] || '1';
  const roots = accounts.filter((a) => !a.parent_id && a.type === type);
  let max = 900;                              // so first suggestion is 1000
  roots.forEach((a) => {
    const parts = a.code.split('-');
    if (parts[0] === p) {
      const n = parseInt(parts[1] || '0', 10);
      if (!isNaN(n)) max = Math.max(max, n);
    }
  });
  const next = Math.floor(max / 100) * 100 + 100;
  return `${p}-${next}`;
};

const emptyForm = () => ({ code: '', name: '', type: 'asset', parent_id: '' });

const toTitle = (s = '') =>
  s
    .toString()
    .toLowerCase()
    .split(' ')
    .map((w) =>
      w.length === 0 ? w
      : /^\d/.test(w) ? w.toUpperCase()
      : ['&', 'dan', 'dari', 'ke', 'di', 'atk'].includes(w) ? w
      : ['ppn', 'bca', 'bni', 'bri', 'atm', 'edc', 'pos', 'sop', 'shopee', 'tiktok', 'hpp'].includes(w) ? w.toUpperCase()
      : w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editingIsSystem, setEditingIsSystem] = useState(false);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState('');

  const startInlineEdit = (a) => {
    setInlineEditId(a.id);
    setInlineEditValue(a.name);
  };
  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineEditValue('');
  };
  const saveInlineEdit = async (a) => {
    const next = inlineEditValue.trim();
    if (!next || next === a.name) return cancelInlineEdit();
    try {
      await updateAccount(a.id, { name: next });
      toast.success('Nama akun diperbarui');
      cancelInlineEdit();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    }
  };
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [applying, setApplying] = useState(false);

  const toggleCollapse = (id) => {
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await listAccounts();
      setAccounts(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ code: suggestCode('asset', '', accounts), name: '', type: 'asset', parent_id: '' });
    setOpen(true);
  };

  const openSub = (parent) => {
    setEditingId(null);
    setForm({
      code: suggestCode(parent.type, parent.id, accounts),
      name: '', type: parent.type, parent_id: parent.id,
    });
    setOpen(true);
  };

  const openEdit = (a) => {
    setForm({ code: a.code, name: a.name, type: a.type, parent_id: a.parent_id || '' });
    setEditingId(a.id);
    setEditingIsSystem(!!a.is_system);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        await updateAccount(editingId, form);
        toast.success('Akun diperbarui');
      } else {
        await createAccount(form);
        toast.success('Akun ditambahkan');
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Hapus akun ${a.code} ${a.name}?`)) return;
    try {
      await deleteAccount(a.id);
      toast.success('Akun dihapus');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const openImport = async () => {
    setImporting(true);
    setImportResult(null);
    setImportOpen(true);
    try {
      const r = await importMasterCoa(true); // dry-run
      setImportResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat preview');
      setImportOpen(false);
    } finally {
      setImporting(false);
    }
  };

  const applyImport = async () => {
    setApplying(true);
    try {
      const r = await importMasterCoa(false);
      setImportResult(r);
      toast.success(`${r.inserted_count} akun baru berhasil ditambahkan`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mengimport');
    } finally {
      setApplying(false);
    }
  };

  const filtered = accounts.filter((a) => {
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q);
  });

  // Build hierarchical display: sort by code, then interleave children right after parents.
  // Respect collapsed set - skip subtree of collapsed parents.
  const hierarchical = useMemo(() => {
    const byParent = new Map();
    filtered.forEach((a) => {
      const k = a.parent_id || '';
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k).push(a);
    });
    byParent.forEach((list) => list.sort((x, y) => x.code.localeCompare(y.code)));
    const idSet = new Set(filtered.map((a) => a.id));
    const out = [];
    const walk = (parentId, depth) => {
      const items = byParent.get(parentId) || [];
      items.forEach((it) => {
        const kids = byParent.get(it.id) || [];
        out.push({ ...it, depth, hasChildren: kids.length > 0 });
        if (!collapsed.has(it.id)) {
          walk(it.id, depth + 1);
        }
      });
    };
    walk('', 0);
    filtered.forEach((a) => {
      if (a.parent_id && !idSet.has(a.parent_id) && !out.find((o) => o.id === a.id)) {
        const kids = byParent.get(a.id) || [];
        out.push({ ...a, depth: 0, hasChildren: kids.length > 0 });
        if (!collapsed.has(a.id)) walk(a.id, 1);
      }
    });
    return out;
  }, [filtered, collapsed]);

  const parentOptions = useMemo(() => {
    return accounts
      .filter((a) => a.type === form.type && a.id !== editingId)
      .sort((x, y) => x.code.localeCompare(y.code));
  }, [accounts, form.type, editingId]);

  const byType = { asset: 0, liability: 0, equity: 0, revenue: 0, expense: 0 };
  accounts.forEach((a) => { byType[a.type] = (byType[a.type] || 0) + 1; });

  return (
    <AccountingLayout>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
              JurnalKu · Chart of Accounts
            </div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight">
              Bagan Akun (COA)
            </h1>
            <p className="text-sm text-stone-600 mt-2">
              Master daftar akun untuk pembukuan double-entry. Akun sistem sudah otomatis di-seed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openImport}
              variant="outline"
              className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid="import-master-coa-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Import Master COA
            </Button>
            <Button
              onClick={openCreate}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="add-account-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Akun Baru
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Object.entries(TYPE_META).map(([k, m]) => (
            <div key={k} className="card-modern p-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${m.color}`}>
                  <m.icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">{m.label}</div>
                  <div className="num text-base font-semibold text-stone-900">{byType[k]}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card-modern p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Cari kode atau nama akun…"
              className="bg-white pl-9"
              data-testid="account-search"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-white w-48" data-testid="account-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tipe</SelectItem>
              {Object.entries(TYPE_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="card-modern overflow-hidden">
          <table className="w-full ledger" data-testid="accounts-table">
            <thead>
              <tr>
                <th style={{ width: '12%' }}>Kode</th>
                <th>Nama Akun</th>
                <th style={{ width: '14%' }}>Tipe</th>
                <th style={{ width: '10%' }}>Saldo Normal</th>
                <th style={{ width: '8%' }}>Status</th>
                <th style={{ width: '10%' }}></th>
              </tr>
            </thead>
            <tbody>
              {hierarchical.map((a) => (
                <tr key={a.id} data-testid={`account-row-${a.code}`}>
                  <td className="font-mono font-semibold text-stone-900">
                    <span style={{ paddingLeft: `${a.depth * 20}px` }} className="inline-flex items-center gap-1">
                      {a.hasChildren ? (
                        <button
                          onClick={() => toggleCollapse(a.id)}
                          className="text-stone-500 hover:text-emerald-700 p-0.5 -ml-1"
                          title={collapsed.has(a.id) ? 'Buka sub akun' : 'Tutup sub akun'}
                          data-testid={`collapse-toggle-${a.code}`}
                        >
                          {collapsed.has(a.id)
                            ? <ChevronRight className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />}
                        </button>
                      ) : a.depth > 0 ? (
                        <CornerDownRight className="w-3.5 h-3.5 text-stone-400" />
                      ) : (
                        <span className="inline-block w-4" />
                      )}
                      {a.code}
                    </span>
                  </td>
                  <td>
                    {inlineEditId === a.id ? (
                      <input
                        autoFocus
                        value={inlineEditValue}
                        onChange={(e) => setInlineEditValue(e.target.value)}
                        onBlur={() => saveInlineEdit(a)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(a); }
                          if (e.key === 'Escape') cancelInlineEdit();
                        }}
                        data-testid={`inline-name-${a.code}`}
                        className="border border-emerald-300 rounded px-2 py-1 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    ) : (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => startInlineEdit(a)}
                        onKeyDown={(e) => { if (e.key === 'Enter') startInlineEdit(a); }}
                        data-testid={`name-cell-${a.code}`}
                        title="Klik untuk mengubah nama"
                        className="text-stone-900 cursor-text hover:bg-emerald-50/60 hover:ring-1 hover:ring-emerald-200 rounded px-1 -mx-1 transition-colors"
                      >
                        {toTitle(a.name)}
                      </span>
                    )}
                    {a.is_header && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-stone-100 text-stone-700 border border-stone-300 px-1.5 py-0.5 rounded font-semibold">
                        Header
                      </span>
                    )}
                    {a.parent_id && !a.is_header && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold">
                        Sub
                      </span>
                    )}
                    {a.group && (
                      <span className="ml-2 text-[10px] text-stone-500">
                        · {a.group}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`inline-block text-[11px] font-semibold px-2 py-1 rounded border ${TYPE_META[a.type]?.color}`}>
                      {TYPE_META[a.type]?.label}
                    </span>
                  </td>
                  <td className="text-xs uppercase tracking-widest font-mono text-stone-600">
                    {a.normal_balance}
                  </td>
                  <td>
                    <span className={`text-[11px] font-medium px-2 py-1 rounded ${a.is_active ? 'text-emerald-800 bg-emerald-50 border border-emerald-200' : 'text-red-800 bg-red-50 border border-red-200'}`}>
                      {a.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openSub(a)}
                        className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                        data-testid={`sub-account-${a.code}`} title="Tambah sub akun">
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`edit-account-${a.code}`}>
                        Ubah
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => remove(a)}
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`delete-account-${a.code}`}
                        title="Hapus akun (hanya bisa jika akun belum dipakai di jurnal)">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {hierarchical.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="text-center text-stone-500 py-8">Tidak ada akun.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Ubah Akun' : 'Akun Baru'}</DialogTitle>
            <DialogDescription>Kode akun harus unik (contoh: 1-1500).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Kode *</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="1-1500"
                className="font-mono"
                disabled={editingId && editingIsSystem}
                data-testid="account-form-code"
              />
              {!editingId && (
                <p className="text-[11px] text-emerald-700 mt-1">
                  Kode otomatis — silakan ubah jika perlu.
                </p>
              )}
              {editingId && editingIsSystem && (
                <p className="text-[11px] text-stone-500 mt-1">
                  Akun sistem: kode terkunci, nama tetap bisa diubah.
                </p>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Nama Akun *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Kas Kecil"
                data-testid="account-form-name"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tipe *</Label>
              <Select
                value={form.type}
                onValueChange={(v) => {
                  const nextParent = form.parent_id;
                  const parentStillValid = accounts.find((p) => p.id === nextParent && p.type === v);
                  const cleanParent = parentStillValid ? nextParent : '';
                  setForm((f) => ({
                    ...f,
                    type: v,
                    parent_id: cleanParent,
                    code: editingId ? f.code : suggestCode(v, cleanParent, accounts),
                  }));
                }}
                disabled={editingId && editingIsSystem}
              >
                <SelectTrigger className="bg-white" data-testid="account-form-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Akun Induk (opsional)
              </Label>
              <Select
                value={form.parent_id || 'ROOT'}
                onValueChange={(v) => {
                  const pid = v === 'ROOT' ? '' : v;
                  setForm((f) => ({
                    ...f,
                    parent_id: pid,
                    code: editingId ? f.code : suggestCode(f.type, pid, accounts),
                  }));
                }}
                disabled={editingId && editingIsSystem}
              >
                <SelectTrigger className="bg-white" data-testid="account-form-parent">
                  <SelectValue placeholder="— Akun utama —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ROOT">— Akun utama (tanpa induk) —</SelectItem>
                  {parentOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-mono text-xs mr-2">{p.code}</span>{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-stone-500 mt-1">
                Sub akun mewarisi tipe akun induk & mendapat kode berurutan (mis. 1-1100 → 1-1101).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="account-form-save">
              <Save className="w-4 h-4 mr-1" /> Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Master COA dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-coa-dialog">
          <DialogHeader>
            <DialogTitle>Import Master COA — CV Play Sport Indonesia</DialogTitle>
            <DialogDescription>
              Preview daftar akun yang akan ditambahkan. Akun yang sudah ada dengan kode sama tidak akan ditimpa.
            </DialogDescription>
          </DialogHeader>

          {importing && (
            <div className="py-10 text-center text-sm text-stone-500">Memuat preview…</div>
          )}

          {!importing && importResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Master</div>
                  <div className="text-2xl font-semibold text-stone-900" data-testid="import-total">{importResult.total_master}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {importResult.total_header} header · {importResult.total_detail} detail
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Akun Baru</div>
                  <div className="text-2xl font-semibold text-emerald-800" data-testid="import-new">{importResult.new_count}</div>
                  <div className="text-[11px] text-emerald-800/70 mt-0.5">akan ditambahkan</div>
                </div>
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-sky-800 font-semibold">Sudah Ada</div>
                  <div className="text-2xl font-semibold text-sky-800" data-testid="import-existing">{importResult.existing_count}</div>
                  <div className="text-[11px] text-sky-800/70 mt-0.5">di-skip (identik)</div>
                </div>
                <div className={`rounded-lg border p-3 ${importResult.conflict_count > 0 ? 'border-amber-200 bg-amber-50' : 'border-stone-200 bg-stone-50'}`}>
                  <div className={`text-[10px] uppercase tracking-widest font-semibold ${importResult.conflict_count > 0 ? 'text-amber-800' : 'text-stone-500'}`}>
                    Konflik Nama
                  </div>
                  <div className={`text-2xl font-semibold ${importResult.conflict_count > 0 ? 'text-amber-800' : 'text-stone-900'}`} data-testid="import-conflicts">
                    {importResult.conflict_count}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">nama beda, kode sama</div>
                </div>
              </div>

              {/* Conflicts detail */}
              {importResult.conflicts && importResult.conflicts.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Konflik Nama (existing dipertahankan, master di-skip)
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-amber-200 text-amber-900">
                          <th className="text-left py-1.5 px-2 font-semibold">Kode</th>
                          <th className="text-left py-1.5 px-2 font-semibold">Nama Existing</th>
                          <th className="text-left py-1.5 px-2 font-semibold">Nama Master</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.conflicts.map((c) => (
                          <tr key={c.code} className="border-b border-amber-100 last:border-0">
                            <td className="py-1.5 px-2 font-mono text-amber-900">{c.code}</td>
                            <td className="py-1.5 px-2 text-stone-700">{c.existing_name}</td>
                            <td className="py-1.5 px-2 text-stone-700">{c.master_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Invalid */}
              {importResult.invalid_count > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-rose-900 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Invalid ({importResult.invalid_count})
                  </div>
                  <pre className="text-[11px] text-rose-900 whitespace-pre-wrap">
                    {JSON.stringify(importResult.invalid, null, 2)}
                  </pre>
                </div>
              )}

              {/* Preview new entries */}
              {importResult.new_entries && importResult.new_entries.length > 0 && (
                <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
                  <div className="px-3 py-2 border-b border-stone-200 bg-stone-50/60 text-xs font-semibold text-stone-700 uppercase tracking-widest">
                    Preview {importResult.new_entries.length} akun baru
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-white border-b border-stone-200">
                        <tr>
                          <th className="text-left py-2 px-2 font-semibold text-stone-500">Kode</th>
                          <th className="text-left py-2 px-2 font-semibold text-stone-500">Nama</th>
                          <th className="text-left py-2 px-2 font-semibold text-stone-500">Tipe</th>
                          <th className="text-left py-2 px-2 font-semibold text-stone-500">Kelompok</th>
                          <th className="text-left py-2 px-2 font-semibold text-stone-500">Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.new_entries.map((e) => (
                          <tr key={e.code} className="border-b border-stone-100 last:border-0">
                            <td className="py-1.5 px-2 font-mono text-stone-800">{e.code}</td>
                            <td className="py-1.5 px-2 text-stone-800">
                              {e.name}
                              {e.is_header && (
                                <span className="ml-2 text-[9px] uppercase tracking-widest bg-stone-100 text-stone-700 border border-stone-300 px-1 py-0.5 rounded font-semibold">
                                  Header
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-stone-600">{e.type}</td>
                            <td className="py-1.5 px-2 text-stone-600">{e.group}</td>
                            <td className="py-1.5 px-2 text-stone-500 uppercase">{e.normal_balance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {importResult.inserted_count > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-900">
                  <CheckCircle2 className="w-4 h-4" />
                  Berhasil menambahkan <b>{importResult.inserted_count}</b> akun baru ke Bagan Akun.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="import-close">
              Tutup
            </Button>
            {importResult && importResult.new_count > 0 && importResult.inserted_count === 0 && (
              <Button
                onClick={applyImport}
                disabled={applying}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="import-apply"
              >
                {applying ? 'Menyimpan…' : `Konfirmasi & Tambah ${importResult.new_count} Akun`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}
